import { getScopedDomain } from '../lib/tenantScope';
// Call Legs — leg-tree CDR viewer.
// Shows a list of calls; click one to see participants, transfer chain, and quality.

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, Phone, PhoneIncoming, PhoneOutgoing, Clock, Activity, X } from 'lucide-react';
import { api } from '../lib/api';

export default function CallLegs() {
  const me = api.me();
  const [filter, setFilter] = useState<{ answered?: string; from_date?: string; to_date?: string }>({});
  const [search, setSearch] = useState('');
  const [picked, setPicked] = useState<number | null>(null);

  const { data: calls = [], isLoading } = useQuery({
    queryKey: ['call-legs', getScopedDomain(me?.domain_uuid), filter, search],
    queryFn: () => {
      if (!getScopedDomain(me?.domain_uuid)) return Promise.resolve([]);
      if (search) return api.callLegs.searchByNumber({ domain_uuid: getScopedDomain(me?.domain_uuid), number: search, limit: 100 });
      return api.callLegs.list({ domain_uuid: getScopedDomain(me?.domain_uuid), ...filter, limit: 100 });
    },
    enabled: !!getScopedDomain(me?.domain_uuid),
  });

  const { data: quality } = useQuery({
    queryKey: ['call-quality-stats', getScopedDomain(me?.domain_uuid)],
    queryFn: () => getScopedDomain(me?.domain_uuid) ? api.callLegs.qualityStats({ domain_uuid: getScopedDomain(me?.domain_uuid) }) : null,
    enabled: !!getScopedDomain(me?.domain_uuid),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Call History</h1>
        <span className="text-xs text-textSecondary">leg-tree CDR (cl_calls)</span>
      </div>

      {quality && (quality as any).sample_count > 0 && (
        <div className="card p-4 grid grid-cols-4 gap-3 text-sm">
          <Stat icon={<Activity size={14} />} label="Avg MOS (caller→PBX)" value={(quality as any).avg_mos_a_to_pbx ?? '—'} />
          <Stat icon={<Activity size={14} />} label="Avg MOS (callee→PBX)" value={(quality as any).avg_mos_b_to_pbx ?? '—'} />
          <Stat icon={<Activity size={14} />} label="Avg loss %" value={(quality as any).avg_loss_a ?? '—'} />
          <Stat icon={<Activity size={14} />} label="Avg jitter" value={`${(quality as any).avg_jitter_a ?? '—'} ms`} />
        </div>
      )}

      <div className="card p-3 flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-1 flex-1 min-w-[200px]">
          <Search size={16} className="text-textSecondary" />
          <input className="input flex-1" placeholder="Search by number…" value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select className="input" value={filter.answered ?? ''} onChange={e => setFilter({ ...filter, answered: e.target.value || undefined })}>
          <option value="">All calls</option>
          <option value="true">Answered only</option>
          <option value="false">Missed only</option>
        </select>
        <input className="input" type="date" value={filter.from_date || ''} onChange={e => setFilter({ ...filter, from_date: e.target.value || undefined })} />
        <input className="input" type="date" value={filter.to_date || ''} onChange={e => setFilter({ ...filter, to_date: e.target.value || undefined })} />
      </div>

      <div className="card p-0 overflow-hidden">
        <table className="table">
          <thead>
            <tr>
              <th>When</th>
              <th>Direction</th>
              <th>From</th>
              <th>To</th>
              <th>Status</th>
              <th>Duration</th>
              <th>Legs</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {calls.map((c: any) => (
              <CallRow key={c.cl_call_id} call={c} onPick={() => setPicked(c.cl_call_id)} />
            ))}
            {!isLoading && calls.length === 0 && (
              <tr><td colSpan={8} className="text-center py-8 text-textSecondary">No calls in range. The cl_calls ingestor populates this from FreeSWITCH events.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {picked && <CallDetailModal clCallId={picked} onClose={() => setPicked(null)} />}
    </div>
  );
}

function CallRow({ call, onPick }: { call: any; onPick: () => void }) {
  const dur = call.talking_dur || call.ringing_dur || '—';
  const direction = call.creation_method === 'transfer' ? 'transfer' : (call.is_answered ? 'answered' : 'missed');
  const Icon = direction === 'missed' ? PhoneIncoming : direction === 'transfer' ? Phone : PhoneOutgoing;
  return (
    <tr className="hover:bg-elevated cursor-pointer" onClick={onPick}>
      <td className="text-xs">{new Date(call.start_time).toLocaleString()}</td>
      <td><Icon size={14} className="inline text-textSecondary" /> <span className="text-xs">{direction}</span></td>
      <td className="font-mono text-sm">
        {call.caller_dn || call.caller_number || '—'}
        {call.caller_name && <div className="text-xs text-textSecondary">{call.caller_name}</div>}
      </td>
      <td className="font-mono text-sm">
        {call.callee_dn || call.callee_number || '—'}
        {call.callee_name && <div className="text-xs text-textSecondary">{call.callee_name}</div>}
      </td>
      <td>
        {call.is_answered
          ? <span className="badge bg-success/20 text-success">answered</span>
          : <span className="badge bg-warning/20 text-warning">{call.termination_reason || 'missed'}</span>}
      </td>
      <td className="text-xs"><Clock size={12} className="inline mr-1" />{String(dur).replace(/00:/, '').split('.')[0]}</td>
      <td><span className="badge">{call.participant_count || 0} party · {call.child_count || 0} children</span></td>
      <td><button className="btn-ghost p-1 text-xs">Detail</button></td>
    </tr>
  );
}

function CallDetailModal({ clCallId, onClose }: { clCallId: number; onClose: () => void }) {
  const { data: detail } = useQuery({
    queryKey: ['call-leg-detail', clCallId],
    queryFn: () => api.callLegs.get(clCallId),
  });
  const { data: tree } = useQuery({
    queryKey: ['call-leg-tree', clCallId],
    queryFn: () => api.callLegs.tree(clCallId),
  });

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="card max-w-3xl w-full max-h-[90vh] overflow-auto p-6 space-y-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold">Call detail · #{clCallId}</h2>
          <button className="btn-ghost p-1" onClick={onClose}><X size={16} /></button>
        </div>

        {!detail ? <div className="text-textSecondary">Loading…</div> : (
          <>
            <Section title="Timing">
              <Row k="Start" v={new Date((detail as any).start_time).toLocaleString()} />
              <Row k="End" v={(detail as any).end_time ? new Date((detail as any).end_time).toLocaleString() : '—'} />
              <Row k="Ringing" v={(detail as any).ringing_dur || '—'} />
              <Row k="Talking" v={(detail as any).talking_dur || '—'} />
              <Row k="Answered" v={(detail as any).is_answered ? 'yes' : 'no'} />
              <Row k="Termination" v={(detail as any).termination_reason || '—'} />
            </Section>

            <Section title={`Participants (${((detail as any).participants || []).length})`}>
              {((detail as any).participants || []).map((p: any) => (
                <div key={p.participant_id} className="flex items-center gap-3 p-2 rounded bg-elevated">
                  <span className="badge">role {p.role}</span>
                  <span className="font-mono">{p.dn || p.caller_number || '—'}</span>
                  <span className="text-textSecondary">{p.display_name || p.firstlastname || ''}</span>
                  <span className="text-xs text-textSecondary ml-auto">{p.dn_type}</span>
                </div>
              ))}
            </Section>

            {((detail as any).quality || []).length > 0 && (
              <Section title="Quality samples">
                <table className="table text-xs">
                  <thead><tr><th>When</th><th>A→PBX MOS</th><th>B→PBX MOS</th><th>Loss A</th><th>Loss B</th><th>RTT A</th><th>Codec</th></tr></thead>
                  <tbody>
                    {((detail as any).quality || []).map((q: any) => (
                      <tr key={q.quality_id}>
                        <td>{new Date(q.time_stamp).toLocaleTimeString()}</td>
                        <td>{q.a_mos_to_pbx?.toFixed?.(2) ?? '—'}</td>
                        <td>{q.b_mos_to_pbx?.toFixed?.(2) ?? '—'}</td>
                        <td>{q.a_rx_loss ?? '—'}</td>
                        <td>{q.b_rx_loss ?? '—'}</td>
                        <td>{q.a_rtt ?? '—'}</td>
                        <td className="font-mono">{q.a_codec || q.b_codec || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Section>
            )}

            {tree && (tree as any).count > 1 && (
              <Section title={`Leg tree — ${(tree as any).count} legs total`}>
                <ol className="space-y-1 text-sm">
                  {((tree as any).legs || []).map((l: any) => (
                    <li key={l.cl_call_id} className="font-mono">
                      <span className="text-textSecondary">{'  '.repeat(l.depth || 0)}└─</span>
                      <span className="badge ml-1">#{l.cl_call_id}</span>
                      <span className="ml-2">{l.creation_method}</span>
                      <span className="ml-2 text-textSecondary">→</span>
                      <span className="ml-2">{l.termination_reason || 'open'}</span>
                    </li>
                  ))}
                </ol>
              </Section>
            )}

            {((detail as any).recordings || []).length > 0 && (
              <Section title="Recordings">
                {((detail as any).recordings || []).map((r: any) => (
                  <div key={r.recording_id} className="flex items-center gap-2 p-2 rounded bg-elevated">
                    <a className="text-primary hover:underline font-mono text-xs flex-1" href={r.recording_url} target="_blank" rel="noreferrer">{r.recording_url}</a>
                    {r.transcribed && <span className="badge bg-success/20 text-success">transcribed</span>}
                    {r.archived && <span className="badge">archived</span>}
                  </div>
                ))}
              </Section>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <h3 className="font-semibold text-sm border-b border-border pb-1">{title}</h3>
      <div className="space-y-1">{children}</div>
    </div>
  );
}
function Row({ k, v }: { k: string; v: any }) {
  return <div className="flex justify-between text-sm py-1"><span className="text-textSecondary">{k}</span><span className="font-mono">{String(v)}</span></div>;
}
function Stat({ icon, label, value }: { icon: React.ReactNode; label: string; value: any }) {
  return (
    <div>
      <div className="text-xs text-textSecondary flex items-center gap-1">{icon}{label}</div>
      <div className="text-lg font-semibold">{value}</div>
    </div>
  );
}
